import { Box, Text } from '@spaceship-fspl/components';
import { TimeScale } from '@spaceship-fspl/helpers';
import { getColor } from '@spaceship-fspl/styles';
import { useBetweenMedia } from '@spaceship-fspl/styles';
import { differenceInDays } from 'date-fns';
import React from 'react';
import styled from 'styled-components';
import { VictoryLine } from 'victory';

import { logError } from '../../helpers/monitoring';
import { dottedBackground } from '../dotted-circle';

export interface LineGraphProps<T> {
  data: T[];
  xAccessor?: (d: T) => number;
  yAccessor?: (d: T) => number;
  renderDate?: (date: Date) => React.ReactNode;
  timeScale?: TimeScale;
}

const GraphWrapper = styled(Box)`
  display: flex;
  width: 100%;
  height: 100%;
`;

const ChartBox = styled(Box)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ChartRow = styled(Box)`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const ChartContainer = styled.svg.attrs({
  role: 'img',
  // Victory charts by default preserves the aspect ratio
  // as set by `height` and `width` props, which default
  // to `height={450}, `width={300}` from the default theme
  // @see: https://github.com/FormidableLabs/victory/blob/master/packages/victory-core/src/victory-theme/grayscale.js#L19
  viewBox: '0 0 450 300',
  // We override this behaivour by providing our own `<svg>`
  // wrapper and setting `height: 100%` rather than `height: auto`
  // and disabling the `preserveAspectRatio` property - this enables
  // the graph to grow/shrink to its parent container
  // @see: https://medium.com/@maheshsenni/responsive-svg-charts-viewbox-may-not-be-the-answer-aaf9c9bc4ca2#8aa6
  preserveAspectRatio: 'none',
  vectorEffect: 'non-scaling-stroke',
})`
  height: 100%;
  width: 100%;
  pointer-events: all;
  ${dottedBackground({ color: 'neutral.085' })}
`;

const YAxisContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-right: 16px;
  min-width: 40px;
  height: 100%;
`;

const YAxisLabel = styled(Text)`
  color: ${getColor('neutral.000')};
  opacity: 0.6;
  text-align: right;
`;

const XAxisLabel = styled(Text)`
  color: ${getColor('neutral.000')};
`;

const XAxisContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding: 16px 0 0;
  margin-left: 40px; // Match YAxisContainer min-width
  width: calc(100% - 40px); // Subtract YAxisContainer width
`;

const formatCurrency = (value: number): string => {
  const formatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(value);
};

const getPointCount = (
  timeScale: TimeScale,
  startDate: Date,
  endDate: Date,
  isMobile: boolean,
): number => {
  if (
    !startDate ||
    !endDate ||
    isNaN(startDate.getTime()) ||
    isNaN(endDate.getTime())
  ) {
    return 0;
  }

  if (isMobile) {
    return 4; // Show 4 points on mobile for all scales
  }

  const daysDiff = differenceInDays(endDate, startDate);
  if (isNaN(daysDiff) || daysDiff < 0) {
    return 0;
  }

  switch (timeScale) {
    case TimeScale.ONE_MONTH:
      return daysDiff <= 7 ? daysDiff + 1 : 8; // Show daily points for weeks, otherwise 8 points
    case TimeScale.THREE_MONTHS:
      return 6;
    case TimeScale.SIX_MONTHS:
      return 6;
    case TimeScale.TWELVE_MONTHS:
      return 6;
    case TimeScale.MAX:
      return 6;
    default:
      return 6;
  }
};

const generatePoints = <T, R>(
  min: T,
  max: T,
  count: number,
  generator: (min: T, max: T, index: number, count: number) => R,
): R[] => {
  if (count <= 0 || !min || !max) {
    return [];
  }
  return Array.from({ length: count }, (_, i) => generator(min, max, i, count));
};

const generateDatePoint = (
  start: number,
  end: number,
  index: number,
  count: number,
): Date => {
  if (
    isNaN(start) ||
    isNaN(end) ||
    isNaN(index) ||
    isNaN(count) ||
    count <= 0
  ) {
    return new Date(0);
  }
  const step = (end - start) / (count - 1);
  return new Date(start + step * index);
};

const generateNumberPoint = (
  min: number,
  max: number,
  index: number,
  count: number,
): number => {
  if (isNaN(min) || isNaN(max) || isNaN(index) || isNaN(count) || count <= 0) {
    return 0;
  }
  const step = (max - min) / (count - 1);
  return min + step * index;
};

const INVALID_DATA_MESSAGE = 'No data available.';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LineGraph = <T extends Record<string, any>>({
  data,
  xAccessor,
  yAccessor,
  renderDate,
  timeScale = TimeScale.SIX_MONTHS,
}: LineGraphProps<T>): React.ReactElement => {
  // Victory unfortunately shits itself when there
  // are no data points, and you need at least two
  // points to draw a line, so here we are!
  const isMobile = useBetweenMedia('xs', 'md');

  // Validate data and accessors
  if (!Array.isArray(data) || data.length < 2 || !xAccessor || !yAccessor) {
    logError('LineGraph', 'Chart data invalid', 'data validation');
    return (
      <Text variant={3} color="neutral.000">
        {INVALID_DATA_MESSAGE}
      </Text>
    );
  }

  // x-axis
  const dates = data
    .map((d) => xAccessor(d))
    .filter((d): d is number => typeof d === 'number' && !isNaN(d));
  if (dates.length < 2) {
    logError('LineGraph', 'Chart data invalid', 'x-axis date validation');
    return (
      <Text variant={3} color="neutral.000">
        {INVALID_DATA_MESSAGE}
      </Text>
    );
  }

  const startDate = new Date(Math.min(...dates));
  const endDate = new Date(Math.max(...dates));

  // y-axis
  const values = data
    .map((d) => yAccessor(d))
    .filter((v): v is number => typeof v === 'number' && !isNaN(v));
  if (values.length < 2) {
    logError('LineGraph', 'Chart data invalid', 'y-axis value validation');
    return (
      <Text variant={3} color="neutral.000">
        {INVALID_DATA_MESSAGE}
      </Text>
    );
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  const pointCount = getPointCount(timeScale, startDate, endDate, isMobile);
  if (pointCount <= 0) {
    logError('LineGraph', 'Chart data invalid', 'axis point count');
    return (
      <Text variant={3} color="neutral.000">
        {INVALID_DATA_MESSAGE}
      </Text>
    );
  }

  const xAxisPoints = generatePoints<number, Date>(
    startDate.getTime(),
    endDate.getTime(),
    pointCount,
    generateDatePoint,
  );

  const yAxisPoints = generatePoints<number, number>(
    minValue,
    maxValue,
    pointCount,
    generateNumberPoint,
  );

  if (xAxisPoints.length === 0 || yAxisPoints.length === 0) {
    logError('LineGraph', 'Chart data invalid', 'axis generation');
    return (
      <Text variant={3} color="neutral.000">
        {INVALID_DATA_MESSAGE}
      </Text>
    );
  }

  return (
    <GraphWrapper>
      <ChartBox>
        <ChartRow>
          <YAxisContainer>
            {yAxisPoints
              .slice()
              .reverse()
              .map((value) => (
                <YAxisLabel key={value} variant={4}>
                  {formatCurrency(value)}
                </YAxisLabel>
              ))}
          </YAxisContainer>
          <ChartContainer>
            <VictoryLine
              data={data}
              x={xAccessor}
              y={yAccessor}
              // Necessary for our `ChartContainer` wrapper:
              // @see: https://formidable.com/open-source/victory/docs/common-props#standalone
              // true -> <VictoryLine /> -> <svg>
              // false -> <VictoryLine /> -> <g>
              standalone={false}
              padding={0}
              domain={{ y: [minValue, maxValue] }}
              style={{
                data: {
                  stroke: getColor('neutral.000'),
                  strokeWidth: 2,
                },
              }}
            />
          </ChartContainer>
        </ChartRow>
        <XAxisContainer>
          {xAxisPoints.map((date) => (
            <XAxisLabel key={date.getTime()} variant={4}>
              {renderDate?.(date)}
            </XAxisLabel>
          ))}
        </XAxisContainer>
      </ChartBox>
    </GraphWrapper>
  );
};
